import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';

const DEFAULT_SETTINGS = {
  appStoreUrl: 'https://apps.apple.com/app/budget4u/id6752348124',
  privacyHtml: '',
  termsHtml: '',
  updatedAt: new Date().toISOString(),
};

const normalizeLegalHtml = (html) => {
  if (!html) return '';
  // Some editors save escaped markup (&lt;p&gt;) instead of real tags.
  if (html.includes('&lt;') && !html.includes('<')) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    return textarea.value;
  }
  return html;
};

const useSiteSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      // Static-only: read settings from a public JSON file bundled with the build.
      const res = await fetch('/site-settings.json', { cache: 'no-cache' });
      if (!res.ok) throw new Error(`Failed to load settings: ${res.status}`);
      const data = await res.json();
      setSettings({ ...DEFAULT_SETTINGS, ...data });
    } catch (err) {
      // Hard fallback: never break the landing page due to missing settings file.
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  return { settings, loading, reload };
};

// Placeholder only if no asset is present. Will be replaced by provided logo files.
const LogoMark = ({ size = 56 }) => (
  <img src="/assets/logo-mark.png" alt="Budget4U" width={size} height={size} style={{ display: 'block' }} />
);

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-[#0B1220] text-white relative overflow-hidden">
      {/* global background accents so they flow under header and footer */}
      <div className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full"
           style={{ background: 'radial-gradient(closest-side, rgba(34,211,238,0.28), rgba(0,0,0,0))' }} />
      <div className="pointer-events-none absolute -bottom-40 -left-40 h-[560px] w-[560px] rounded-full"
           style={{ background: 'radial-gradient(closest-side, rgba(59,130,246,0.22), rgba(0,0,0,0))' }} />
      <header className="max-w-6xl mx-auto px-4 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src="/favicon.png" alt="Budget4U" height={32} style={{ height: 32 }} />
        </Link>
        <nav className="flex items-center gap-6 text-sm text-white/80">
          <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
          <Link to="/support" className="hover:text-white">Support</Link>
          <Link to="/terms-of-conditions" className="hover:text-white">Terms of Use</Link>
        </nav>
      </header>

      {children}

      <footer className="max-w-6xl mx-auto px-4 py-10 text-sm text-white/70">
        <div className="pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p>Contact us: <a className="text-white hover:underline" href="mailto:info@budget4u.app">info@budget4u.app</a></p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/support" className="hover:text-white">Support</Link>
            <Link to="/terms-of-conditions" className="hover:text-white">Terms of Use</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

const AppStoreButton = ({ href }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-3 rounded-xl px-5 py-3 bg-gradient-to-b from-[#1F1F1F] to-black border border-white/10 shadow-[0_6px_20px_rgba(0,0,0,0.35)] hover:brightness-110 transition"
    aria-label="Download on the App Store"
  >
    <div className="h-6 w-6 rounded-md bg-white/10 grid place-items-center">
      <span aria-hidden="true" className="text-lg"></span>
    </div>
    <div className="text-left leading-tight">
      <div className="text-[11px] uppercase tracking-widest text-white/70">Download on the</div>
      <div className="text-[18px] font-semibold">App Store</div>
    </div>
  </a>
);

const Landing = ({ settings }) => {
  return (
    <Layout>
      <main className="relative">

        <section className="max-w-6xl mx-auto px-4 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src="/assets/logo-horizontal.png" alt="Budget4U" className="h-10 md:h-12" />
            </div>
            <p className="text-lg md:text-xl text-white/80 max-w-xl">
              AI-powered personal finance assistant for iOS. Track expenses, set budgets, and get smart tips to save more.
            </p>

            <div className="mt-8 flex items-center gap-4 justify-center md:justify-start">
              <AppStoreButton href={settings?.appStoreUrl || '#'} />
            </div>

            <p className="mt-6 text-white/70 text-sm">
              Need help? Contact us at <a className="text-white hover:underline" href="mailto:support@budget4u.app">support@budget4u.app</a>.
            </p>
          </div>

          <div className="relative">
            <div className="relative mx-auto w-3/4 md:w-2/5 bg-black rounded-[32px] p-2 shadow-2xl border border-white/10">
              <div className="absolute top-2 left-1/2 -translate-x-1/2 h-2 w-24 bg-black/80 rounded-b-2xl" />
              <img
                src="/assets/preview.png"
                alt="Budget4U app preview"
                className="rounded-[28px] w-full h-auto block"
              />
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
};

const Page = ({ title, html }) => {
  const content = normalizeLegalHtml(html);
  const hasH1 = /<h1[\s>]/i.test(content);
  return (
    <Layout>
      <main className="max-w-3xl mx-auto px-4 py-14">
        {!hasH1 && (
          <h1 className="text-4xl font-semibold tracking-tight mb-6">{title}</h1>
        )}
        <article
          className="prose prose-invert max-w-none prose-headings:scroll-mt-24 prose-h1:text-white prose-h2:text-white prose-h3:text-white prose-p:text-white/85 prose-li:text-white/85 prose-strong:text-white prose-a:text-cyan-300 hover:prose-a:text-cyan-200 prose-ul:list-disc prose-ol:list-decimal prose-li:marker:text-white/60 prose-pre:bg-white/5 prose-code:text-white"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </main>
    </Layout>
  );
};

const Support = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (idx) => setOpenIndex((prev) => (prev === idx ? null : idx));

  const faqs = [
    {
      q: 'How do I sign in?',
      a: (
        <ul className="list-disc list-inside space-y-1">
          <li>Open the app and choose one of the options on the first screen:</li>
          <li>Sign in with Apple (iOS)</li>
          <li>Sign in with Google</li>
          <li>Sign in with email and password</li>
          <li>If you don’t have an account yet, tap “Create account” and follow the prompts.</li>
        </ul>
      ),
    },
    {
      q: 'How do I restore purchases/subscription?',
      a: (
        <ul className="list-disc list-inside space-y-1">
          <li>Open the app → go to Settings (gear icon in the bottom tabs).</li>
          <li>Tap “Restore Purchases.” The app will check the App Store receipt and re‑activate Pro on this device for the account that owns the subscription.</li>
          <li>If you still don’t see Pro after a few seconds, keep the app open and try again. You can also tap “Manage Subscriptions” to review/cancel your App Store subscription.</li>
        </ul>
      ),
    },
    {
      q: 'How do I delete my account?',
      a: (
        <ul className="list-disc list-inside space-y-1">
          <li>Open the app → Settings.</li>
          <li>Scroll down and tap “Delete Account.”</li>
          <li>Confirm in the dialog to permanently delete your account and app data from our servers.</li>
          <li><strong>Important:</strong> Deleting your account does not cancel an active App Store subscription. To cancel, go to iPhone Settings → Apple ID → Subscriptions, or tap “Manage Subscriptions” in the app’s Settings.</li>
        </ul>
      ),
    },
    {
      q: 'I changed devices or reinstalled the app. How do I get Pro back?',
      a: (
        <ul className="list-disc list-inside space-y-1">
          <li>Make sure you are signed in with the same App Store Apple ID used for the purchase.</li>
          <li>In the app, go to Settings → “Restore Purchases.”</li>
          <li>If the subscription was bought on a different app account, sign in to that account first, then restore.</li>
        </ul>
      ),
    },
    {
      q: 'I can’t sign in or see an error',
      a: (
        <ul className="list-disc list-inside space-y-1">
          <li>Ensure you have a stable internet connection.</li>
          <li>Update the app to the latest version from the App Store.</li>
          <li>If you use Google/Apple sign in, make sure you approve the browser prompt and return to the app.</li>
          <li>If the issue persists, contact support with your device model, iOS version, and the time of the failed attempt.</li>
        </ul>
      ),
    },
    {
      q: 'How do I change my password (email accounts)?',
      a: (
        <ul className="list-disc list-inside space-y-1">
          <li>Open the app → on the sign in screen tap “Forgot password?” and follow the instructions sent to your email.</li>
        </ul>
      ),
    },
  ];

  return (
    <Layout>
      <main className="max-w-3xl mx-auto px-4 py-14">
        <h1 className="text-4xl font-semibold tracking-tight mb-3">Budget4U Support</h1>
        <p className="text-white/80 mb-8">Have a question or issue? Write to us — we respond within 24–48 hours. Email: <a className="text-cyan-300 hover:text-cyan-200" href="mailto:support@budget4u.app">support@budget4u.app</a></p>

        <section className="space-y-3">
          {faqs.map((item, idx) => (
            <div key={idx} className="bg-white/5 border border-white/10 rounded-xl">
              <button
                type="button"
                onClick={() => toggle(idx)}
                aria-expanded={openIndex === idx}
                className="w-full text-left px-4 py-3 flex items-center justify-between"
              >
                <span className="font-medium">{item.q}</span>
                <span className="ml-4 text-white/70">{openIndex === idx ? '−' : '+'}</span>
              </button>
              {openIndex === idx && (
                <div className="px-4 pb-4 text-white/85">
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </section>
      </main>
    </Layout>
  );
};

function App() {
  const { settings, loading } = useSiteSettings();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1220] text-white grid place-items-center">
        <div className="text-center opacity-80">
          <LogoMark size={48} />
          <p className="mt-4">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing settings={settings} />} />
        <Route path="/privacy-policy" element={<Page title="Privacy Policy" html={settings?.privacyHtml || ''} />} />
        <Route path="/terms-of-conditions" element={<Page title="Terms of Use" html={settings?.termsHtml || ''} />} />
        <Route path="/support" element={<Support />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;


